<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import type { CsvRow } from "@/models/csv-row";
import type { TableColumn, TableColumnOptions } from "@/models/table-column";
import { csvService } from "@/services/csv-service";
import { paginateItems } from "@/utils/array-utils";
import { mdiMenuDown, mdiMenuSwap, mdiMenuUp } from "@mdi/js";
import { get, sortBy } from "lodash-es";
import { computed, nextTick, onBeforeMount, ref, toRefs } from "vue";

export interface Props {
  columns: TableColumn[];
  items: any[];
  loading?: boolean;
  pagination?: boolean;
  exportCsv?: boolean;
  searchFilter?: string;
  sortColumn?: any;
  showSearch?: boolean;
  disableClick?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showSearch: true,
  searchFilter: undefined,
  sortColumn: {
    key: null as string | null,
    asc: true,
  },
  disableClick: false,
});

const { columns, items, searchFilter, sortColumn, showSearch, disableClick } =
  toRefs(props);

onBeforeMount(async () => {
  tableLoading.value = true;
  await setupTableData();
  tableLoading.value = false;
});

const showSearchField = ref(true);
const sorted = ref({
  key: null as string | null,
  asc: true,
});

async function setupTableData() {
  if (searchFilter?.value && tableLoading.value) {
    searchQuery.value = searchFilter.value;
  }

  if (!showSearch?.value) {
    showSearchField.value = false;
  }

  if (sortColumn?.value && tableLoading.value) {
    sorted.value = sortColumn.value;
  }
}

const emit = defineEmits<{
  (e: "click:row", data: any, filter: string, sorted: any): void;
}>();

const tableLoading = ref(true);

const paginationData = computed(() => {
  let itemsToReturn = items.value;

  if (sorted.value.key) {
    itemsToReturn = sortBy(items.value, [sorted.value.key]);

    if (!sorted.value.asc) {
      itemsToReturn.reverse();
    }
  }

  if (searchQuery.value) {
    const lowerSearchQuery = searchQuery.value.toLowerCase();

    itemsToReturn = itemsToReturn.filter((v) =>
      JSON.stringify(v).toLocaleLowerCase().includes(lowerSearchQuery),
    );
  }
  return paginateItems(itemsToReturn, page.value, pageSize.value || 25);
});

const searchQuery = ref("");
const searchQueryData = computed({
  get: () => (searchQuery.value ? searchQuery.value : "Sök"),
  set: (val) => {
    if (val) {
      const searchFilterToSave = searchFilter?.value;
      if (searchFilterToSave && tableLoading.value) {
        searchQuery.value = searchFilterToSave;
      }
    }
  },
});

const displaysItems = computed(() => paginationData.value.total > 0);
const pageSizes = computed(() => [25, 50, 75]);
const hasFewItems = computed(
  () => paginationData.value.total < (pageSizes.value?.[0] || 25),
);

const page = ref(1);
const pageSize = ref(pageSizes.value?.[0] || 25);

function onClickRow(item: any, filter: any) {
  if (disableClick.value) {
    return;
  }
  const test = JSON.stringify(sorted.value);
  emit("click:row", item, filter, JSON.parse(test));
}

function onClickSort(column: TableColumn) {
  if (sorted.value.key === column.key) {
    sorted.value.asc = !sorted.value.asc;
  } else {
    sorted.value.key = column.key;
    sorted.value.asc = true;
  }
}

async function onClickPageSize(value: number) {
  pageSize.value = value;

  await nextTick();
  if (page.value > paginationData.value.pages) {
    page.value = 1;
  }
}

const previousPageDisabled = computed(() => page.value <= 1);
function onClickPreviousPage() {
  if (page.value > 1) {
    page.value--;
  }
}

const nextPageDisabled = computed(
  () => page.value >= paginationData.value.pages,
);
function onClickNextPage() {
  if (page.value < paginationData.value.pages) {
    page.value++;
  }
}

function onClickExport() {
  const rows = items.value.map((item) => {
    const values = columns.value.map((column) =>
      column.getCsvValue(itemValue(item, column.key)),
    );
    return { values } as CsvRow;
  });

  const csvContent = csvService.getCsvContent(rows);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  const today = new Date()
    .toLocaleString("sv")
    .replace(/ /g, "T")
    .replace(/:/g, "-");
  anchor.setAttribute("href", objectUrl);
  anchor.setAttribute("download", `export-${today}.csv`);
  anchor.click();
}

function columnClass(options?: TableColumnOptions) {
  return {
    "has-text-right": options?.right,
  };
}

function itemValue(item: any, key: string) {
  return get(item, key, undefined);
}
</script>

<template>
  <div
    class="columns is-multiline is-variable is-1 is-justify-content-space-between is-align-items-center"
  >
    <div v-if="showSearchField" class="column is-narrow">
      <div class="field">
        <label for="" class="label is-sr-only">Sök</label>
        <div class="control">
          <input
            v-model="searchQuery"
            type="search"
            class="input is-light"
            :placeholder="searchQueryData"
          />
        </div>
      </div>
    </div>

    <div v-if="displaysItems" class="column is-narrow">
      <div class="columns is-multiline is-variable is-1">
        <div
          v-if="!hasFewItems"
          class="column is-narrow py-1 is-flex is-align-items-center"
        >
          <p>
            Sida {{ page }} / {{ paginationData.pages }} ({{
              paginationData.pagedItems.length
            }}
            / {{ paginationData.total }})
          </p>
        </div>

        <div v-if="!hasFewItems" class="column is-narrow py-1">
          <button
            v-for="size in pageSizes"
            :key="size"
            class="button"
            @click="onClickPageSize(size)"
          >
            {{ size }}
          </button>
        </div>

        <div v-if="!hasFewItems" class="column is-narrow py-1">
          <button
            class="button"
            :disabled="previousPageDisabled"
            @click="onClickPreviousPage"
          >
            Föregående
          </button>
          <button
            class="button"
            :disabled="nextPageDisabled"
            @click="onClickNextPage"
          >
            Nästa
          </button>
        </div>

        <div v-if="exportCsv" class="column is-narrow py-1">
          <button class="button" @click="onClickExport">Exportera</button>
        </div>
      </div>
    </div>
  </div>

  <div class="table-container">
    <template v-if="loading">
      <table class="table is-fullwidth">
        <tbody>
          <tr>
            <td :colspan="columns.length">
              <div class="has-text-centered">Laddar...</div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <template v-else>
      <table
        v-if="displaysItems"
        class="table is-hoverable is-fullwidth"
        :class="{ 'click-is-disabled': disableClick }"
      >
        <thead>
          <tr>
            <th
              v-for="(column, index) in columns"
              :key="index"
              :class="columnClass(column.options)"
              @click="column.options?.sort ? onClickSort(column) : undefined"
            >
              <span class="icon-text">
                <span>{{ column.title }}</span>

                <span v-if="column.options?.sort" class="icon">
                  <template v-if="sorted.key === column.key">
                    <app-icon
                      v-show="sorted.asc"
                      :icon="mdiMenuDown"
                      size="24"
                    />
                    <app-icon
                      v-show="!sorted.asc"
                      :icon="mdiMenuUp"
                      size="24"
                    />
                  </template>

                  <template v-else>
                    <app-icon :icon="mdiMenuSwap" size="24" />
                  </template>
                </span>
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(item, itemIndex) in paginationData.pagedItems"
            :key="itemIndex"
            @click="onClickRow(item, searchQuery)"
          >
            <td
              v-for="(column, columnIndex) in columns"
              :key="columnIndex"
              :class="columnClass(column.options)"
            >
              <slot
                :name="'column:' + column.key"
                v-bind="{
                  index: columnIndex,
                  value: itemValue(item, column.key),
                  item,
                  ...column,
                }"
              >
                <template v-if="column.options?.renderer">
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <span v-html="column.options.renderer(item)" />
                </template>
                <template v-else>
                  {{
                    column?.options?.formatter
                      ? column?.options?.formatter(itemValue(item, column.key))
                      : itemValue(item, column.key)
                  }}
                </template>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>

      <template v-else>
        <table class="table is-fullwidth">
          <tbody>
            <tr>
              <td :colspan="columns.length">
                <div class="has-text-centered">Listan är tom.</div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.click-is-disabled tr {
  cursor: default !important;
}
</style>
